import { useState } from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import { normalizePortfolioItem } from "@/hooks/portfolio/usePortfolioNormalizer";
import { showErrorToast } from "@/utils/toastUtils";
import { toast } from 'sonner';

export function usePortfolioItems(
  initialItems: PortfolioItem[] | null,
  onPortfolioChange: (portfolioItems: PortfolioItem[]) => void,
  isVerified: boolean = false
) {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleOperation = async (
    operation: () => Promise<void>,
    errorMessage: string
  ) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      await operation();
    } catch (error) {
      console.error(`Error in portfolio operation:`, error);
      showErrorToast(errorMessage, "Please try again later");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddItem = (newItem: PortfolioItem) => {
    if (!isVerified && (initialItems?.length || 0) >= 3) {
      toast.error('Non-featured users can only add up to 3 portfolio items.');
      return;
    }
    handleOperation(async () => {
      const normalizedItem = normalizePortfolioItem(newItem);
      
      // Don't automatically set featured status or change status
      // Let the unified portfolio system handle featured status
      const itemToAdd = { 
        ...normalizedItem, 
        is_featured: false, // Don't auto-feature new items
        status: normalizedItem.status // Keep the original status (draft/published)
      };
      
      const updatedItems = [...(initialItems || []), itemToAdd];
      onPortfolioChange(updatedItems);
      toast.success("Your project has been added successfully.");
    }, "Failed to add project");
  };

  const handleUpdateItem = (updatedItem: PortfolioItem) => {
    if (!initialItems) return;

    handleOperation(async () => {
      const itemIndex = initialItems.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (itemIndex === -1) {
        throw new Error("Could not find item to update");
      }

      const normalizedItem = normalizePortfolioItem(updatedItem);
      const updatedItems = [...initialItems];
      updatedItems[itemIndex] = normalizedItem;
      onPortfolioChange(updatedItems);
      toast.success("Your project has been updated successfully.");
    }, "Failed to update project");
  };

  const handleRemoveItem = (index: number) => {
    if (!initialItems || index < 0 || index >= initialItems.length) return;

    handleOperation(async () => {
      let updatedItems = [...initialItems];
      updatedItems.splice(index, 1);

      // After removal, ensure only one published project is featured
      const published = updatedItems.filter(item => item.status === 'published');
      if (published.length === 1) {
        updatedItems = updatedItems.map(item =>
          item.id === published[0].id
            ? { ...item, is_featured: true }
            : { ...item, is_featured: false }
        );
      } else if (published.length === 0) {
        updatedItems = updatedItems.map(item => ({ ...item, is_featured: false }));
      }

      onPortfolioChange(updatedItems);
      toast.success("Your project has been deleted.");
    }, "Failed to delete project");
  };

  const handleRenameItem = (id: string, newTitle: string) => {
    if (!initialItems) return;

    handleOperation(async () => {
      const itemIndex = initialItems.findIndex((item) => item.id === id);
      if (itemIndex === -1) {
        throw new Error("Could not find item to rename");
      }

      const normalizedItem = normalizePortfolioItem(initialItems[itemIndex]);
      const updatedItems = [...initialItems];
      updatedItems[itemIndex] = {
        ...normalizedItem,
        title: newTitle,
      };
      onPortfolioChange(updatedItems);
      toast.success("Project title has been updated.");
    }, "Failed to rename project");
  };

  const handleReorderItems = (reorderedItems: PortfolioItem[]) => {
    if (!initialItems) return;

    handleOperation(async () => {
      // Assign 'order' property based on new array index
      const reorderedAndNormalized = reorderedItems.map((item, index) => ({
        ...normalizePortfolioItem(item),
        order: index,
      }));
      onPortfolioChange(reorderedAndNormalized);
      toast.success("The order of your projects has been updated.");
    }, "Failed to reorder projects");
  };

  const handleSetFeaturedItem = (id: string) => {
    if (!initialItems) return;

    handleOperation(async () => {
      let updatedItems = initialItems.map((item) => {
        if (item.id === id) {
          // Set the selected item as featured and published
          return { 
            ...item, 
            is_featured: true,
            status: "published" as const // Ensure the item is published when featured
          };
        } else {
          // Unfeature all other items
          return { ...item, is_featured: false };
        }
      });

      // Move the featured item to the first position
      const featuredItem = updatedItems.find((item) => item.id === id);
      if (featuredItem) {
        updatedItems = [featuredItem, ...updatedItems.filter((item) => item.id !== id)];
      }

      onPortfolioChange(updatedItems);
      toast.success("This project is now featured.");
    }, "Failed to set featured project");
  };

  const handleUpdateStatus = (id: string, status: "draft" | "published") => {
    if (!initialItems) return;

    handleOperation(async () => {
      let updatedItems = [...initialItems];
      const itemIndex = updatedItems.findIndex((item) => item.id === id);
      if (itemIndex === -1) {
        throw new Error("Could not find item to update");
      }

      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        status,
        ...(status === "draft" ? { is_featured: false } : {}),
      };

      // After status update, ensure only one published project is featured
      const published = updatedItems.filter(item => item.status === 'published');
      if (published.length === 1) {
        updatedItems = updatedItems.map(item =>
          item.id === published[0].id
            ? { ...item, is_featured: true }
            : { ...item, is_featured: false }
        );
      } else if (published.length === 0) {
        updatedItems = updatedItems.map(item => ({ ...item, is_featured: false }));
      }

      onPortfolioChange(updatedItems);
      toast.success(
        status === "published"
          ? "Your project is now published."
          : "Your project has been saved as a draft."
      );
    }, "Failed to update project status");
  };

  return {
    handleAddItem,
    handleUpdateItem,
    handleRemoveItem,
    handleRenameItem,
    handleReorderItems,
    handleSetFeaturedItem,
    handleUpdateStatus,
    isProcessing,
  };
}
